import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { ThemeContext } from 'styled-components'
import Image from '../../../helpers/Image/Image'
import Callouts from '../../../helpers/Callouts'
import Roundel from '../../../helpers/Roundel/Roundel'
import { agnostifyUrl } from '../../../helpers/utils'
import { createGolContentDataAttributes, joinContentLinkTitles } from '../../../helpers/tracking'
import * as Styled from './M053.styles'

/**
 * - M053 is a component where authors can display content that traditionally
 * sits on the CLPs or feature pages that will be useful to many customers on their journey:
 * - Inspirational content (e.g. content and link to /Features page)
 * - Buying guides (e.g. content and link to /guide page)
 */

// The link and image prop changes are temporary and will need a clean up once migration is done and when nav team adopted new props.
const M053 = ({
  image,
  heading,
  links,
  bodyCopy,
  callouts,
  legalCopy,
  backgroundColor,
  roundel,
  link,
  ctaText,
  ctaStyle,
  consumingGridType,
  positionInGrid,
  citrus
}) => {
  const theme = useContext(ThemeContext)
  const roundelBackground = roundel?.type === 'new' ? theme.color.content.new : theme.color.content.sale
  const isNectarRoundel = roundel?.type === 'nectar'

  const linksArray = links || [link]
  const [mainLink] = linksArray

  const showCTAContainer = ctaText || mainLink?.copy || legalCopy

  const mainWrapperClickable = mainLink?.url && !mainLink?.copy
  const imageWrapperClickable = mainLink?.copy && mainLink?.url

  // This should be removed after m053 LM release
  const imageURL = image.source || image.url

  const getGOLDataAttributes = (linkTitle, url, action) => {
    if (citrus) return {}

    return createGolContentDataAttributes(
      consumingGridType,
      `M053 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      action === 'Load' ? joinContentLinkTitles(linksArray, [], 'M053') : linkTitle,
      url,
      heading,
      action,
      true
    )
  }

  const addGolTracking = (evt, linkTitle, linkUrl) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, linkUrl)
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (evt, linkTitle, linkUrl, linkTarget) => {
    addGolTracking(evt, linkTitle, linkUrl)
    window.open(agnostifyUrl(linkUrl), linkTarget)
  }

  const imageWrapper = () => {
    return (
      <Styled.ImageWrapper
        data-test='m053-image-wrapper'
        backgroundColor={backgroundColor}
        onClick={imageWrapperClickable ? (evt) => addGolTracking(evt, mainLink?.title, mainLink?.url) : undefined}
        as={imageWrapperClickable ? 'a' : undefined}
        href={imageWrapperClickable ? agnostifyUrl(mainLink?.url) : undefined}
        target={mainWrapperClickable ? mainLink?.target : undefined}
        isCitrus={citrus}
      >
        <Image alt={image.altText} src={imageURL} smart />
        {Object.keys(roundel).length > 0 && (
          <Roundel component='m053' roundel={roundel} backgroundColor={roundelBackground} />
        )}
      </Styled.ImageWrapper>
    )
  }

  const textWrapper = () => {
    const citrusFooter = roundel?.content?.footer
    const citrusTerms = roundel?.content?.terms
    return (
      <Styled.TextWrapper data-test='m053-text-wrapper' isNectarRoundel={isNectarRoundel}>
        <Styled.Heading
          data-test={`m053-${isNectarRoundel ? 'nectar-heading' : 'heading'}`}
          isOffer={roundel?.type === 'offer'}
          isNectarRoundel={isNectarRoundel}
        >
          {heading}
        </Styled.Heading>
        {(bodyCopy || roundel?.body) && (
          <Styled.BodyCopy
            data-test={`m053-${isNectarRoundel ? 'nectar-' : ''}body-copy`}
            isNectarRoundel={isNectarRoundel}
          >
            {bodyCopy}
          </Styled.BodyCopy>
        )}
        {(citrusFooter || citrusTerms) && (
          <Styled.Footer data-test='m053-nectar-footer'>
            {citrusFooter && `${citrusFooter} `}
            {citrusTerms && (
              <a
                data-test='m053-nectar-terms-and-conditions'
                href={citrusTerms.link}
                target='_blank'
                title='Nectar Terms & Conditions'
                rel='noreferrer'
              >
                {citrusTerms.text}
              </a>
            )}
          </Styled.Footer>
        )}
        {callouts.length > 0 && <Callouts parentComponent='m053' callouts={callouts} noMargin />}
      </Styled.TextWrapper>
    )
  }

  const legalCopyMarkup = () => ({ __html: legalCopy })

  return (
    <Styled.Container
      {...getGOLDataAttributes('', mainLink?.url, 'Load')}
      data-test='m053-container'
      role='button'
      onClick={
        mainWrapperClickable ? (evt) => ctaClick(evt, mainLink?.title, mainLink?.url, mainLink?.target) : undefined
      }
      tabIndex={mainWrapperClickable && 0}
      as={mainWrapperClickable ? 'a' : undefined}
      href={mainWrapperClickable ? agnostifyUrl(mainLink?.url) : undefined}
      target={mainWrapperClickable ? mainLink?.target : undefined}
    >
      {imageWrapper()}
      <Styled.TextAndCTAWrapper data-test='m053-text-and-cta-container' isNectarRoundel={isNectarRoundel}>
        {textWrapper()}
        {showCTAContainer && (
          <Styled.CTAAndLegalCopyContainer data-test='m053-cta-container' isSecondaryCta={linksArray.length > 1}>
            <Styled.CTAContainer role='group'>
              {linksArray.map((linkElement, index) => (
                <Styled.CTAButton
                  key={`m053-link-${index}`}
                  data-test={`m053-${linkElement.style || ctaStyle}-cta-${index}`}
                  title={linkElement.title}
                  kind={linkElement.style || ctaStyle}
                  full={false}
                  onClick={(evt) => ctaClick(evt, linkElement.title, linkElement.url, linkElement.target)}
                  isLinkCta={linkElement.style === 'link' || ctaStyle === 'link'}
                >
                  {linkElement.copy || ctaText}
                </Styled.CTAButton>
              ))}
            </Styled.CTAContainer>
            {legalCopy && <Styled.LegalCopy data-test='m053-legal-copy' dangerouslySetInnerHTML={legalCopyMarkup()} />}
          </Styled.CTAAndLegalCopyContainer>
        )}
      </Styled.TextAndCTAWrapper>
    </Styled.Container>
  )
}

M053.propTypes = {
  /** Required text for heading */
  heading: PropTypes.string.isRequired,
  /** Required image */
  image: PropTypes.shape({
    /** Required image URL */
    url: PropTypes.string.isRequired,
    /** Required alt text for the image */
    altText: PropTypes.string.isRequired
  }).isRequired,
  /** Array of links to be displayed */
  links: PropTypes.arrayOf(
    PropTypes.shape({
      /** Link URL */
      url: PropTypes.string,
      /** Link title, can be seen when hovering over the CTA */
      title: PropTypes.string,
      /** Option of opening in a new tab, defaults to _blank  */
      target: PropTypes.oneOf(['_blank', '_self']),
      /** Text for CTA */
      copy: PropTypes.string,
      /** CTA style which can either be link, primary or secondary */
      style: PropTypes.oneOf(['link', 'primary', 'secondary'])
    })
  ),
  /** Optional body copy text */
  bodyCopy: PropTypes.string,
  /** Optional legalCopy text for more information */
  legalCopy: PropTypes.string,
  /** Array with callout values to be displayed */
  callouts: PropTypes.array,
  /** Background color for image only used for citrus variant of component */
  backgroundColor: PropTypes.string,
  /** Roundel prop only used for citrus variant of component */
  roundel: PropTypes.shape({
    /** Type of roundel which can either be new or offer */
    type: PropTypes.oneOf(['new', 'offer', 'nectar']),
    /** Promotional text which will be the heading if roundel type is offer */
    header: PropTypes.string
  }),
  /** Name of the containing grid */
  consumingGridType: PropTypes.string,
  /** Position in teh containing grid */
  positionInGrid: PropTypes.number,
  /** Informs if the source of the components is citrus or not */
  citrus: PropTypes.bool
}

M053.defaultProps = {
  callouts: [],
  bodyCopy: '',
  legalCopy: '',
  backgroundColor: '',
  roundel: {},
  consumingGridType: 'Isolated'
}

export default M053
